// Qualified business income deduction worksheet (IRC §199A; Form 8995 / Form 8995-A, incl. Schedules A and C).
// All amounts are ANNUAL. The engine's PartyInputs.qbid is monthly — compute.ts / taxes.ts convert.
import type { IrsStatus, QbidWorksheet, TaxYear } from '@divorceos/schema';

/**
 * §199A(e)(2) threshold amounts and §199A(b)(3)(B) / (d)(3) phase-in ranges (inflation-adjusted):
 *  - 2022: Rev. Proc. 2021-45 §3.27 — $340,100 MFJ / $170,050 MFS and all others (phase-in end $440,100 / $220,050)
 *  - 2023: Rev. Proc. 2022-38 §3.27 — $364,200 MFJ / $182,100 MFS and all others ($464,200 / $232,100)
 *  - 2024: Rev. Proc. 2023-34 §3.27 — $383,900 MFJ / $191,950 MFS and all others ($483,900 / $241,950)
 *  - 2025: Rev. Proc. 2024-40 §3.27 — $394,600 MFJ / $197,300 MFS and all others ($494,600 / $247,300)
 * The range is $100,000 for a joint return and $50,000 for every other return (MFS uses the single figures).
 */
export const QBID_THRESHOLDS: Record<TaxYear, { single: number; joint: number }> = {
  2022: { single: 170_050, joint: 340_100 },
  2023: { single: 182_100, joint: 364_200 },
  2024: { single: 191_950, joint: 383_900 },
  2025: { single: 197_300, joint: 394_600 },
  //  - 2026: Rev. Proc. 2025-32 — $403,500 MFJ / $201,775 MFS and all others; P.L. 119-21 widened the phase-in range to $75,000 / $150,000 (fully phased at $276,775 / $553,500).
  2026: { single: 201_775, joint: 403_500 },
  //  - 2027: PROVISIONAL — Rev. Proc. not yet published; carried from 2026 as a placeholder (phase-in range stays the P.L. 119-21 $75k/$150k). Replace on publication (CPA-03).
  2027: { single: 201_775, joint: 403_500 },
};
export const QBID_PHASE_IN_RANGE = { single: 50_000, joint: 100_000 } as const;
/** P.L. 119-21 widened the §199A phase-in range starting 2026. */
export const QBID_PHASE_IN_RANGE_2026 = { single: 75_000, joint: 150_000 } as const;
export const QBID_RATE = 0.2;

export function qbidThreshold(status: IrsStatus, year: TaxYear): { threshold: number; range: number } {
  const t = QBID_THRESHOLDS[year] ?? QBID_THRESHOLDS[2025];
  const r = year >= 2026 ? QBID_PHASE_IN_RANGE_2026 : QBID_PHASE_IN_RANGE;
  return status === 'MFJ' ? { threshold: t.joint, range: r.joint } : { threshold: t.single, range: r.single };
}

export interface QbidContext {
  /** Taxable income before the QBI deduction (Form 1040 line 11 − line 12). */
  taxableIncomeBeforeQbid: number;
  /** Net capital gain (qualified dividends + net long-term gain), Form 8995 line 12 / 8995-A line 33. */
  netCapitalGain: number;
  status: IrsStatus;
  year: TaxYear;
}

export interface QbidBusinessResult {
  name: string;
  sstb: boolean;
  /** Schedule A applicable percentage (1 for non-SSTBs). */
  applicablePercent: number;
  /** QBI after the applicable percentage and after the Schedule C netting of losses. */
  qbi: number;
  w2Wages: number;
  ubia: number;
  /** 20 % of the (netted) QBI. */
  twentyPercentQbi: number;
  /** Greater of 50 % of W-2 wages or 25 % of W-2 wages + 2.5 % of UBIA. */
  wageLimit: number;
  /** Phase-in reduction (Part III), 0 below the threshold or when the wage limit does not bind. */
  reduction: number;
  /** QBI component of this business. */
  component: number;
}

export interface QbidResult {
  /** Annual deduction (Form 8995 line 15 / 8995-A line 39). */
  deduction: number;
  lines: Record<string, number>;
  perBusiness: QbidBusinessResult[];
}

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const pos = (v: number) => Math.max(0, v);

/**
 * Form 8995 (taxable income ≤ threshold) / Form 8995-A (above) for one return. Pure.
 * Steps: SSTB applicable percentage (Schedule A), netting of negative QBI and the prior-year loss carryforward
 * across the positive-QBI businesses in proportion to their QBI (Schedule C), per-business 20 % with the W-2 wage /
 * UBIA limitation phased in over the range (Part II / III), REIT dividends + PTP income at 20 %, and the overall
 * income limitation 20 % × (taxable income − net capital gain).
 */
export function computeQbid(ws: QbidWorksheet, ctx: QbidContext): QbidResult {
  const ti = num(ctx.taxableIncomeBeforeQbid);
  const { threshold, range } = qbidThreshold(ctx.status, ctx.year);
  const excess = Math.min(range, pos(ti - threshold));
  const phase = range > 0 ? excess / range : 1; // 0 at/below the threshold, 1 at/above threshold + range
  const aboveThreshold = ti > threshold;
  const applicable = 1 - phase; // Schedule A: SSTB applicable percentage

  const biz = (ws.businesses ?? []).map((b) => {
    const pct = b.sstb ? applicable : 1;
    return { name: b.name ?? '', sstb: !!b.sstb, pct, qbi: num(b.qbi) * pct, w2: pos(num(b.w2Wages)) * pct, ubia: pos(num(b.ubia)) * pct };
  });

  // Schedule C: losses (current-year negative QBI and the carryforward) are allocated to the positive businesses.
  const carry = pos(num(ws.lossCarryforward));
  const positive = biz.filter((b) => b.qbi > 0).reduce((a, b) => a + b.qbi, 0);
  const negative = biz.filter((b) => b.qbi < 0).reduce((a, b) => a + b.qbi, 0) - carry;
  const netQbi = positive + negative;
  const netted = biz.map((b) => {
    if (b.qbi <= 0) return 0;
    if (netQbi <= 0) return 0;
    return b.qbi + negative * (b.qbi / positive);
  });

  const perBusiness: QbidBusinessResult[] = biz.map((b, i) => {
    const qbi = netted[i];
    const twenty = QBID_RATE * qbi;
    const wageLimit = Math.max(0.5 * b.w2, 0.25 * b.w2 + 0.025 * b.ubia);
    let reduction = 0;
    let component = twenty;
    if (aboveThreshold && qbi > 0 && wageLimit < twenty) {
      if (phase >= 1) { component = wageLimit; reduction = twenty - wageLimit; } else { reduction = (twenty - wageLimit) * phase; component = twenty - reduction; }
    }
    return { name: b.name, sstb: b.sstb, applicablePercent: b.pct, qbi, w2Wages: b.w2, ubia: b.ubia, twentyPercentQbi: twenty, wageLimit, reduction, component };
  });

  const qbiComponent = pos(perBusiness.reduce((a, b) => a + b.component, 0));
  const reitPtp = num(ws.reitPtp);
  const reitComponent = QBID_RATE * pos(reitPtp);
  const beforeLimit = qbiComponent + reitComponent;
  const incomeLimit = QBID_RATE * pos(ti - pos(num(ctx.netCapitalGain)));
  const deduction = Math.min(beforeLimit, incomeLimit);
  const lines: Record<string, number> = {
    taxableIncomeBeforeQbid: ti,
    threshold,
    phaseInRange: range,
    phaseInPercent: phase,
    usesForm8995A: aboveThreshold ? 1 : 0,
    totalPositiveQbi: positive,
    totalNegativeQbi: negative + carry,
    priorYearLossCarryforward: -carry,
    netQbi,
    qbiComponent,
    reitPtp,
    reitPtpComponent: reitComponent,
    componentsTotal: beforeLimit,
    netCapitalGain: pos(num(ctx.netCapitalGain)),
    incomeLimitation: incomeLimit,
    deduction,
    lossCarryforwardNextYear: netQbi < 0 ? netQbi : 0,
    reitPtpCarryforwardNextYear: reitPtp < 0 ? reitPtp : 0,
  };
  return { deduction, lines, perBusiness };
}
